/**
 * useFcmToken — manages FCM token lifecycle and reflects the canonical
 * subscription state from the backend.
 *
 * Flow:
 * 1. On mount: TanStack Query fetches /devices → derives isSubscribed,
 *    activeRegistrationId, hasRegisteredDevices.
 * 2. requestPermission(): browser prompt → on grant → getToken() → POST /devices
 *    → invalidate devices query (no local state mirroring).
 * 3. subscribeWithExistingPermission(): same as #2 but skips the browser prompt
 *    when Notification.permission is already "granted" (covers the
 *    refresh-after-grant and backend-only-unsubscribe cases).
 * 4. revokePermission(): POST /devices/deactivate using the registrationId from
 *    the current query data → invalidate devices query.
 * 5. usePermissionState() syncs browser permission across tabs via the
 *    Permissions API + visibilitychange/focus, and invalidates the devices
 *    query so cross-tab subscribes appear without a manual refresh.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  subscribeToFcm,
  unsubscribeFromFcm,
  fetchFirebaseConfig,
} from "../api/fcmApi";
import { getFcmDevicesQueryOptions } from "../api/fcmQueryOptions";
import { notificationKeys } from "@/shared/lib/queryKeys";
import { logger } from "@/shared/lib/logger";

export interface UseFcmTokenReturn {
  permission: NotificationPermission;
  fcmToken: string | null;
  isSubscribed: boolean;
  /** True if the backend has ≥1 registered device (possibly another browser). */
  hasRegisteredDevices: boolean;
  loading: boolean;
  error: string | null;
  requestPermission: () => Promise<boolean>;
  /** Subscribe without re-prompting; requires Notification.permission === "granted". */
  subscribeWithExistingPermission: () => Promise<boolean>;
  revokePermission: () => Promise<void>;
}

function getPermission(): NotificationPermission {
  if (typeof Notification === "undefined") return "default";
  return Notification.permission;
}

const TOKEN_ROTATION_CHECK_KEY = "fcm:tokenCheckAt";
const TOKEN_ROTATION_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 h

/**
 * Checks whether the FCM registration token in the backend matches what
 * Firebase would return now. If they differ (Firebase auto-rotation happened),
 * re-subscribes so push delivery doesn't silently break.
 *
 * Throttled to once per 24 h via sessionStorage to avoid hitting Firebase on
 * every focus event.
 */
async function checkTokenRotation(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  if (getPermission() !== "granted") return;

  const lastCheck = Number(sessionStorage.getItem(TOKEN_ROTATION_CHECK_KEY) ?? 0);
  if (Date.now() - lastCheck < TOKEN_ROTATION_INTERVAL_MS) return;
  sessionStorage.setItem(TOKEN_ROTATION_CHECK_KEY, String(Date.now()));

  const cached = queryClient.getQueryData<Array<{ registrationId: string; active: boolean }>>(
    notificationKeys.devices()
  );
  const activeSub = cached?.find((d) => d.active);
  if (!activeSub) return;

  try {
    const current = await getFcmTokenFromFirebase();
    if (current && current !== activeSub.registrationId) {
      await subscribeToFcm({ registrationId: current, type: "web" });
      await queryClient.invalidateQueries({ queryKey: notificationKeys.devices() });
    }
  } catch {
    // Swallow — the check runs again on next focus tomorrow.
  }
}

/**
 * Tracks Notification.permission and re-reads it when the tab regains focus or
 * when the Permissions API fires a "change" event (covers cross-tab grants).
 * Also invalidates the devices query so the toggle reflects subscriptions made
 * in other tabs, and triggers a throttled FCM token-rotation check on focus.
 */
function usePermissionState(): NotificationPermission {
  const [perm, setPerm] = useState<NotificationPermission>(getPermission);
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;
    let status: PermissionStatus | null = null;

    const sync = () => {
      setPerm(getPermission());
      queryClient.invalidateQueries({ queryKey: notificationKeys.devices() });
    };

    const syncWithRotationCheck = () => {
      sync();
      // Fire-and-forget — token rotation check runs in the background.
      void checkTokenRotation(queryClient);
    };

    if (typeof navigator !== "undefined" && navigator.permissions) {
      navigator.permissions
        .query({ name: "notifications" as PermissionName })
        .then((s) => {
          if (cancelled) return;
          status = s;
          s.addEventListener("change", sync);
        })
        .catch(() => {
          // Some browsers (older Safari) reject "notifications" — silently fall
          // back to visibilitychange/focus only.
        });
    }

    document.addEventListener("visibilitychange", syncWithRotationCheck);
    window.addEventListener("focus", syncWithRotationCheck);

    return () => {
      cancelled = true;
      status?.removeEventListener("change", sync);
      document.removeEventListener("visibilitychange", syncWithRotationCheck);
      window.removeEventListener("focus", syncWithRotationCheck);
    };
  }, [queryClient]);

  return perm;
}

export function useFcmToken(): UseFcmTokenReturn {
  const queryClient = useQueryClient();
  const permission = usePermissionState();
  const [mutationLoading, setMutationLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: devices, isFetching } = useQuery(getFcmDevicesQueryOptions);

  // Derived canonical state from the backend device list.
  const { activeSub, isSubscribed, hasRegisteredDevices } = useMemo(() => {
    const list = devices ?? [];
    const active = list.find((d) => d.active) ?? null;
    return {
      activeSub: active,
      isSubscribed: active !== null,
      hasRegisteredDevices: list.length > 0,
    };
  }, [devices]);

  const fcmToken = activeSub?.registrationId ?? null;

  /**
   * Performs the token-fetch + POST /devices side of subscription. Caller is
   * responsible for ensuring Notification.permission is already "granted".
   */
  const subscribeCurrentBrowser = useCallback(async (): Promise<boolean> => {
    try {
      const token = await getFcmTokenFromFirebase();
      if (!token) return false;
      await subscribeToFcm({ registrationId: token, type: "web" });
      await queryClient.invalidateQueries({ queryKey: notificationKeys.devices() });
      return true;
    } catch (err) {
      logger.error("[useFcmToken] subscribeCurrentBrowser failed:", err);
      setError(String(err));
      return false;
    }
  }, [queryClient]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    setMutationLoading(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") return false;
      return await subscribeCurrentBrowser();
    } catch (err) {
      setError(String(err));
      return false;
    } finally {
      setMutationLoading(false);
    }
  }, [subscribeCurrentBrowser]);

  const subscribeWithExistingPermission = useCallback(async (): Promise<boolean> => {
    if (getPermission() !== "granted") return false;
    setMutationLoading(true);
    setError(null);
    try {
      return await subscribeCurrentBrowser();
    } finally {
      setMutationLoading(false);
    }
  }, [subscribeCurrentBrowser]);

  const revokePermission = useCallback(async () => {
    if (!fcmToken) return;
    setMutationLoading(true);
    try {
      await unsubscribeFromFcm(fcmToken);
      await queryClient.invalidateQueries({ queryKey: notificationKeys.devices() });
    } catch (err) {
      setError(String(err));
    } finally {
      setMutationLoading(false);
    }
  }, [fcmToken, queryClient]);

  return {
    permission,
    fcmToken,
    isSubscribed,
    hasRegisteredDevices,
    loading: mutationLoading || isFetching,
    error,
    requestPermission,
    subscribeWithExistingPermission,
    revokePermission,
  };
}

/**
 * Gets FCM token from Firebase.
 * Registers firebase-messaging-sw.js explicitly so navigator.serviceWorker.ready
 * is guaranteed to resolve before getToken() is called.
 */
async function getFcmTokenFromFirebase(): Promise<string | null> {
  const { getToken, getMessaging, isSupported } = await import("firebase/messaging");
  const { initializeApp, getApps } = await import("firebase/app");

  // Check FCM support before doing any network work
  if (!(await isSupported())) {
    return null;
  }

  // Fetch config from backend — vapidKey included so no env var needed
  const config = await fetchFirebaseConfig();

  if (!config.vapidKey) {
    throw new Error("vapidKey not returned by backend — check FIREBASE_VAPID_KEY setting");
  }

  // Initialize app once
  const app = getApps().length === 0
    ? initializeApp(config)
    : getApps()[0]!;

  const messaging = getMessaging(app);

  try {
    // Explicitly register the Firebase SW — idempotent if already installed.
    // Using navigator.serviceWorker.ready alone would hang forever if no SW
    // has been registered yet.
    // Base-aware SW path so it resolves under subdirectory deploys, e.g.
    // /portal/firebase-messaging-sw.js when VITE_BASE_PATH=/portal/.
    // import.meta.env.BASE_URL keeps its trailing slash, so it joins the
    // filename cleanly and doubles as the registration scope.
    const base = import.meta.env.BASE_URL;
    const registration = await navigator.serviceWorker.register(
      `${base}firebase-messaging-sw.js`,
      { scope: base }
    );
    const token = await getToken(messaging, {
      serviceWorkerRegistration: registration,
      vapidKey: config.vapidKey,
    });
    return token || null;
  } catch (err) {
    logger.error("[useFcmToken] getToken failed:", err);
    const msg = (err as Error).message ?? "";
    if (
      msg.includes("messaging/only-available-in-window") ||
      msg.includes("messaging/registration-default-interface-only-available") ||
      msg.includes("messaging/fcm-not-available") ||
      msg.includes("messaging_only_available_in_window") ||
      msg.includes("failed-service-worker-registration") ||
      msg.includes("messaging/service-worker-not-found")
    ) {
      return null;
    }
    throw err;
  }
}
